import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const num = (i) => String(i + 1).padStart(2, "0");
const Val = ({ v, href }) => (href ? <a href={href} className="text-[#1f44e0] hover:underline break-words">{v}</a> : <span>{v}</span>);

function DataCard({ data }) {
  return (
    <div className="rounded-2xl bg-white border border-[#0f2544]/10 overflow-hidden" data-testid="legal-datos">
      {data.title && <p className="px-5 sm:px-6 py-3 bg-[#0f2544] text-[0.7rem] font-bold tracking-[0.2em] uppercase text-[#e6c364]">{data.title}</p>}
      <dl className="divide-y divide-[#0f2544]/8">
        {data.rows.map(([k, v, href]) => (
          <div key={k} className="grid sm:grid-cols-[230px_1fr] gap-1 sm:gap-4 px-5 sm:px-6 py-3 text-sm">
            <dt className="text-[#8a8a83]">{k}</dt>
            <dd className="text-[#0f2544] font-medium"><Val v={v} href={href} /></dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Address({ a }) {
  return (
    <div className="rounded-2xl bg-[#0f2544] text-white p-6 sm:p-7 grid sm:grid-cols-2 gap-6" data-testid="legal-contacto">
      <address className="not-italic leading-relaxed">
        <p className="font-serif text-xl font-semibold">{a.lines[0]}</p>
        {a.lines.slice(1).map((l) => <p key={l} className="text-white/75 text-sm">{l}</p>)}
      </address>
      <div className="space-y-2 text-sm">
        {a.contact.map(([k, v, href]) => (
          <p key={k}><span className="block text-[0.65rem] font-bold tracking-[0.2em] uppercase text-[#e6c364]">{k}</span><a href={href} className="hover:underline break-words">{v}</a></p>
        ))}
      </div>
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((it) => {
        const [term, text, href] = Array.isArray(it) ? it : [null, it];
        return (
          <li key={term || text} className="flex gap-3">
            <span className="mt-[0.6rem] w-1.5 h-1.5 rounded-full bg-[#1f44e0] shrink-0" />
            <span className="min-w-0 break-words">{term && <strong className="text-[#0f2544] font-semibold">{term}{href ? "" : ":"} </strong>}{href ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#1f44e0] hover:underline">{text}</a> : text}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Block({ b }) {
  if (typeof b === "string") return <p>{b}</p>;
  if (b.list) return <List items={b.list} />;
  if (b.data) return <DataCard data={b.data} />;
  if (b.address) return <Address a={b.address} />;
  if (b.email) return <a href={`mailto:${b.email}`} className="inline-block rounded-full bg-white border border-[#1f44e0]/30 px-5 py-2 text-[#1f44e0] font-semibold hover:bg-[#eef1ff] transition-colors">{b.email}</a>;
  if (b.sub) return <div><h3 className="font-serif text-lg font-semibold text-[#0f2544]">{b.sub[0]}</h3><p className="mt-1">{b.sub[1]}</p></div>;
  if (b.cookies) return <button type="button" onClick={() => window.dispatchEvent(new Event("hv:open-cookies"))} className="inline-flex rounded-full bg-[#0f2544] px-6 py-3 text-xs font-semibold tracking-[0.12em] uppercase text-white hover:bg-[#1f44e0] transition-colors" data-testid="legal-open-cookies">Configurar mis cookies</button>;
  if (b.link) return <p><Link to={b.link[1]} className="text-[#1f44e0] font-semibold hover:underline">{b.link[0]}</Link></p>;
  return null;
}

function Section({ s, i }) {
  return (
    <section id={s.id} className="scroll-mt-28 py-10 border-t border-[#0f2544]/10 first:border-t-0 first:pt-0" data-testid={`legal-section-${s.id}`}>
      <div className="flex items-baseline gap-4">
        <span className="font-serif text-sm font-bold text-[#1f44e0] tabular-nums">{num(i)}</span>
        <h2 className="font-serif text-2xl md:text-[1.7rem] font-semibold text-[#0f2544] leading-tight">{s.title}</h2>
      </div>
      <div className="mt-4 space-y-4 text-[#4a4a45] leading-relaxed sm:pl-9">
        {s.body.map((b, k) => <Block key={k} b={b} />)}
      </div>
    </section>
  );
}

function Index({ sections }) {
  return (
    <nav className="lg:sticky lg:top-28 rounded-2xl bg-[#eceae4] p-5" aria-label="Índice" data-testid="legal-indice">
      <p className="text-[0.7rem] font-bold tracking-[0.2em] uppercase text-[#0f2544] mb-3">Índice</p>
      <ol className="space-y-1.5">
        {sections.map((s, i) => (
          <li key={s.id}><a href={`#${s.id}`} className="flex gap-2.5 text-sm text-[#4a4a45] hover:text-[#1f44e0] transition-colors"><span className="tabular-nums text-[#8a8a83]">{num(i)}</span>{s.title}</a></li>
        ))}
      </ol>
    </nav>
  );
}

export default function LegalDoc({ doc, testId }) {
  return (
    <>
      <Seo title={doc.title} description={doc.description} />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20" data-testid={testId}>
        <header className="max-w-3xl">
          <p className="hv-overline">Legal</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0f2544] mt-3">{doc.title}</h1>
          <p className="text-[#4a4a45] mt-4">Editorial Harvest — www.editorialharvest.com</p>
          <p className="inline-block mt-4 rounded-full bg-white border border-[#0f2544]/10 px-4 py-1.5 text-xs text-[#4a4a45]" data-testid="legal-actualizacion">Última actualización: {doc.updated}</p>
        </header>
        <div className="mt-12 grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-16 items-start">
          <Index sections={doc.sections} />
          <article className="min-w-0 max-w-3xl">
            {doc.sections.map((s, i) => <Section key={s.id} s={s} i={i} />)}
            {doc.closing && <div className="mt-4"><Address a={doc.closing} /></div>}
            <p className="mt-6 pt-8 border-t border-[#0f2544]/10 text-sm text-[#8a8a83]">{doc.copyright || "© 2026 Editorial Harvest — Neho Consulting Group Sàrl. Todos los derechos reservados."}</p>
          </article>
        </div>
      </div>
    </>
  );
}
